import { apiService } from '@clarix/api-client';
import type { PresignedUrlItem } from '@clarix/shared-types';
import type {
  CalendarPrefsWire,
  CompleteBody,
  CreateQuickMeetingBody,
  CreateQuickMeetingResponse,
  ExtendMinutes,
  ExtendOptionsDTO,
  HeartbeatBody,
  HeartbeatResponse,
  NextMeetingDTO,
  RecordingStatusDTO,
  RecordingSummary,
  ReuploadResponse,
  WireEvent,
} from './types';

// The shell's calendar-api calls (API-CONTRACT §18.3, §11.1). Workspace-scoped like
// sidebar/providers/calendarProviders.tsx; never through the calendar remote.

const base = (slug: string) => `/${slug}/calendar`;
const recording = (slug: string, id: string) => `${base(slug)}/recordings/${encodeURIComponent(id)}`;
const event = (slug: string, id: string) => `${base(slug)}/events/${encodeURIComponent(id)}`;

const RECORDER_CALL = /\/calendar\/(?:quick-meetings|recordings\/[^/?#]+\/(?:upload-urls|heartbeat|complete|reupload))(?:[?#]|$)/;

/** The recorder's own calls run behind whatever page is open, so a denial on one never walls it. */
export function isShellRecorderCall(url: string): boolean {
  return RECORDER_CALL.test(url);
}

export const quickMeetingApi = {
  status: (slug: string) => apiService.get<RecordingStatusDTO>(`${base(slug)}/recordings/status`),
  next: (slug: string) => apiService.get<NextMeetingDTO>(`${base(slug)}/events/next`),
  preferences: (slug: string) => apiService.get<CalendarPrefsWire>(`${base(slug)}/preferences`),

  create: (slug: string, body: CreateQuickMeetingBody) =>
    apiService.post<CreateQuickMeetingResponse>(`${base(slug)}/quick-meetings`, body),
  uploadUrls: (slug: string, id: string, body: { upload_id: string; next_part_number: number }) =>
    apiService.post<{ presigned_urls: PresignedUrlItem[] }>(`${recording(slug, id)}/upload-urls`, body),
  heartbeat: (slug: string, id: string, body: HeartbeatBody) =>
    apiService.post<HeartbeatResponse>(`${recording(slug, id)}/heartbeat`, body),
  complete: (slug: string, id: string, body: CompleteBody) =>
    apiService.post<RecordingSummary>(`${recording(slug, id)}/complete`, body),
  /** §18.3 v6.6.1: a fresh multipart upload when S3 lost the one the client holds. */
  reupload: (slug: string, id: string, body: { upload_id: string }) =>
    apiService.post<ReuploadResponse>(`${recording(slug, id)}/reupload`, body),
  get: (slug: string, id: string) => apiService.get<RecordingSummary>(recording(slug, id)),
  abort: (slug: string, id: string) =>
    apiService.post<{ discarded: true }>(`${recording(slug, id)}/abort`, {}),
  process: (slug: string, id: string) =>
    apiService.post<RecordingSummary>(`${recording(slug, id)}/process`, {}),
  retry: (slug: string, id: string) =>
    apiService.post<RecordingSummary>(`${recording(slug, id)}/retry`, {}),

  endEvent: (slug: string, id: string, body: { version_no: number }) =>
    apiService.post<WireEvent>(`${event(slug, id)}/end`, body),
  extendEvent: (slug: string, id: string, body: { minutes: ExtendMinutes; version_no: number }) =>
    apiService.post<WireEvent>(`${event(slug, id)}/extend`, body),
  extendOptions: (slug: string, id: string) =>
    apiService.get<ExtendOptionsDTO>(`${event(slug, id)}/extend-options`),
};
